import { ArrowBack } from "@mui/icons-material";
import { Divider, IconButton, List, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import { ObservationMediaDisplay } from "~/modules/mapEditor/components/ObservationMediaDisplay";
import FormTemplateDialogEditor from "~/shared/components/FormTemplate/FormTemplateDialogEditor";
import FormTemplateForm from "~/shared/components/FormTemplate/FormTemplateForm";
import RenderIf from "~/shared/components/RenderIf";

import MapEditorFeatureDetailTitle from "../MapEditorFeatureDetailTitle";
import ObservationStatus from "../ObservationStatus";
import { MapEditorObservationPanelEcologicalFunctionFragment } from "./components/EcologicalFunctionCard/fragments.generated";
import { LinkedEcologicalFunctions } from "./components/LinkedEcologicalFunctions";
import { ObservationMediaPointCard } from "./components/ObservationMediaPointCard";
import { MapEditorObservationPanelObservationMediaPointFragment } from "./components/ObservationMediaPointCard/fragments.generated";
import { MapEditorObservationPanelObservationFragment } from "./fragments.generated";
import { useSuspenseObservationReadOnlyQuery } from "./useSuspenseObservationReadOnlyQuery";

export interface MapEditorObservationPanelReadOnlyProps {
    backHref: string;
    createHrefToEcologicalFunction: (element: MapEditorObservationPanelEcologicalFunctionFragment) => string;
    createHrefToObservationMediaPointCard: (element: MapEditorObservationPanelObservationMediaPointFragment) => string;
    mapEditorFeatureId: string;
    title: string;
}

export function MapEditorObservationPanelReadOnly({
    backHref,
    createHrefToEcologicalFunction,
    createHrefToObservationMediaPointCard,
    mapEditorFeatureId,
    title,
}: MapEditorObservationPanelReadOnlyProps) {
    const { t } = useTranslation("mapEditor");

    const { data } = useSuspenseObservationReadOnlyQuery(mapEditorFeatureId);
    const observation = data.mapEditorFeatureGetById.data as MapEditorObservationPanelObservationFragment;

    return (
        <>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" to={backHref} relative="path">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6">{title}</Typography>
            </MapPanelHeader>
            <MapPanelScrollableArea>
                <Stack gap={2} padding={2}>
                    <MapEditorFeatureDetailTitle
                        title={observation.researchType.name}
                        user={observation.createdByUser}
                        titleSlot={<RenderIf payload={observation.status} render={s => <ObservationStatus status={s} />} />}
                    />

                    <Stack>
                        <Typography variant="caption">{t("featureDetailObservation.observationType")}</Typography>
                        <Typography>{observation.type.name}</Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="caption">{t("featureDetailObservation.species")}</Typography>
                        <Typography>{observation.species.name}</Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="caption">{t("featureDetailObservation.createdAt")}</Typography>
                        <Typography>{parseStringToDate(observation.createdAt).toLocaleString()}</Typography>
                    </Stack>

                    {observation.mediaPoints.length > 0 && (
                        <List>
                            {observation.mediaPoints.map(element => (
                                <ObservationMediaPointCard
                                    createHref={createHrefToObservationMediaPointCard}
                                    key={element.id}
                                    mediaPoint={element}
                                />
                            ))}
                        </List>
                    )}

                    {observation.medias.length > 0 && <ObservationMediaDisplay media={observation.medias} />}

                    {observation.preciseLocation && (
                        <>
                            <Divider>
                                <Typography>{t("featureDetailObservation.preciseLocation")}</Typography>
                            </Divider>
                            <FormTemplateDialogEditor
                                form={data.settingsFormTemplateGetMasterForm}
                                values={observation.preciseLocation}
                                onChange={() => {}}
                                readOnly={true}
                            />
                        </>
                    )}

                    {observation.type?.formTemplate && (
                        <>
                            <Divider>
                                <Typography>{t("featureDetailObservation.observationDetails")}</Typography>
                            </Divider>
                            <FormTemplateForm
                                form={observation.type.formTemplate}
                                values={observation.answers ?? {}}
                                onChange={() => {}}
                                readOnly={true}
                            />
                        </>
                    )}

                    {observation.ecologicalFunctions.length > 0 && (
                        <LinkedEcologicalFunctions
                            createTo={createHrefToEcologicalFunction}
                            elements={observation.ecologicalFunctions}
                        />
                    )}
                </Stack>
            </MapPanelScrollableArea>
        </>
    );
}
